import type { ClaimFilters, ClaimRow, SortState } from '../types/claims.types';
import { EMPTY_FILTERS } from '../types/claims.types';
import {
  missesWindow,
  NO_DATE_BOUNDS,
  type ClaimDateBounds,
  type DateBounds,
} from './claim-date-bounds';

export const DATE_RANGE_MESSAGE = 'The From date cannot be later than the To date.';
export const DATE_RANGE_INCOMPLETE_MESSAGE = 'Enter both a From and a To date to filter by a range.';
export const DATE_RANGE_OUT_OF_BOUNDS_MESSAGE =
  'Invalid date range is selected. Please select a valid date range.';
export const AMOUNT_MESSAGE = 'Enter an amount as a number, for example 125.50.';
export const DIGITS_MESSAGE = 'Enter digits only.';
export const ALPHANUMERIC_MESSAGE = 'Enter letters and digits only.';

/** True when nothing is set, which is what keeps the Reset button disabled (US-014 AC-005). */
export function hasActiveFilters(filters: ClaimFilters): boolean {
  return (
    filters.statuses.length > 0 ||
    Object.entries(filters).some(
      ([key, value]) => key !== 'statuses' && typeof value === 'string' && value.trim().length > 0,
    )
  );
}

/**
 * Both date ranges are validated the same way: reversed (US-014 AC-004), half-open (US-026 AC-002), and
 * outside the window the loaded claims cover (PP-011). The message names the field and what is expected,
 * and it clears as soon as the value is corrected, because it is derived from the draft on every render
 * rather than latched on submit (AC-003).
 *
 * `bounds` defaults to unbounded so a caller with no claims in hand — and the range rules on their own —
 * behaves exactly as before.
 */
export function validateDateRanges(
  filters: ClaimFilters,
  bounds: ClaimDateBounds = NO_DATE_BOUNDS,
): {
  readonly service: string | null;
  readonly payment: string | null;
} {
  return {
    service: dateRangeError(filters.serviceDateFrom, filters.serviceDateTo, bounds.service),
    payment: dateRangeError(filters.paymentDateFrom, filters.paymentDateTo, bounds.payment),
  };
}

/**
 * Character-set rules for the text filters (US-026 AC-001).
 *
 * These match on a SUBSTRING, so a full-format rule would be wrong: '963' is a perfectly good way to
 * filter by NPI, and demanding ten digits would refuse it. What is checked is what the field can never
 * contain — a letter in a numeric identifier, punctuation in a code — which is enough to tell the
 * provider a value cannot match anything without taking partial search away from them.
 */
export function validateTextFilters(filters: ClaimFilters): Record<TextFilterKey, string | null> {
  return {
    claimNumber: charsetError(filters.claimNumber, DIGITS, DIGITS_MESSAGE),
    npi: charsetError(filters.npi, DIGITS, DIGITS_MESSAGE),
    checkNumber: charsetError(filters.checkNumber, DIGITS, DIGITS_MESSAGE),
    icn: charsetError(filters.icn, ALPHANUMERIC, ALPHANUMERIC_MESSAGE),
    procCode: charsetError(filters.procCode, ALPHANUMERIC, ALPHANUMERIC_MESSAGE),
    svcCode: charsetError(filters.svcCode, ALPHANUMERIC, ALPHANUMERIC_MESSAGE),
  };
}

/** Amount filters are minimums, so anything that is not a number cannot be applied (US-026 AC-001). */
export function validateAmounts(filters: ClaimFilters): {
  readonly billedAmount: string | null;
  readonly medicareAllowedAmount: string | null;
  readonly paidAmount: string | null;
} {
  return {
    billedAmount: amountError(filters.billedAmount),
    medicareAllowedAmount: amountError(filters.medicareAllowedAmount),
    paidAmount: amountError(filters.paidAmount),
  };
}

/**
 * Checked in this order deliberately: a reversed or half-open range is a more specific thing to say than
 * "invalid", and both of those messages were fixed by an accepted story. The bounds rule only speaks for
 * the case neither of them covers — a well-formed range that does not touch the loaded claims at all. A
 * range only has to OVERLAP the window: asking for 08/01 to 10/20 when the claims end on 09/30 is a
 * perfectly good question, and refusing it kept the Search button disabled for a filter that matches.
 */
function dateRangeError(from: string, to: string, bounds: DateBounds | null): string | null {
  if (isReversed(from, to)) {
    return DATE_RANGE_MESSAGE;
  }

  if (from.length > 0 !== to.length > 0) {
    return DATE_RANGE_INCOMPLETE_MESSAGE;
  }

  return missesWindow(from, to, bounds) ? DATE_RANGE_OUT_OF_BOUNDS_MESSAGE : null;
}

/** Location and Reason are free prose and carry no character rule. */
export type TextFilterKey = 'claimNumber' | 'npi' | 'checkNumber' | 'icn' | 'procCode' | 'svcCode';

const DIGITS = /^[0-9]*$/;
const ALPHANUMERIC = /^[A-Za-z0-9]*$/;

function charsetError(value: string, pattern: RegExp, message: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 || pattern.test(trimmed) ? null : message;
}

function amountError(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }

  const parsed = Number(trimmed);
  return Number.isFinite(parsed) && parsed >= 0 ? null : AMOUNT_MESSAGE;
}

/** Free-text search across the visible columns (US-010 AC-001). */
export function matchesKeyword(claim: ClaimRow, keyword: string): boolean {
  const term = keyword.trim().toLowerCase();
  if (term.length === 0) {
    return true;
  }

  return [
    claim.claimNumber,
    claim.npi,
    claim.icn,
    claim.location,
    claim.procCode,
    claim.svcCode,
    claim.status,
    claim.checkNumber,
    claim.reason,
  ]
    .filter((value): value is string => typeof value === 'string')
    .some((value) => value.toLowerCase().includes(term));
}

/**
 * Whether a claim belongs in the grid for the current search and filters. The claim qualifies when it
 * or any of its lines satisfies ALL of them at once: the header and its lines are separate records
 * upstream, and a payment date or check number often exists on the lines only.
 */
export function matchesView(claim: ClaimRow, keyword: string, filters: ClaimFilters): boolean {
  return [claim, ...claim.lines].some(
    (row) => matchesKeyword(row, keyword) && matchesFilters(row, filters),
  );
}

/**
 * The lines of a claim worth showing for the current search and filters. A claim is listed because one
 * of its lines matched, so showing every other line beside it makes the grid answer a different question
 * than the one asked: filter by a Reason and the expanded claim still lists lines with other reasons.
 * With no search and no filter every line stays, and so does a claim none of whose lines match on their
 * own (the header was what matched).
 */
export function visibleLines(
  claim: ClaimRow,
  keyword: string,
  filters: ClaimFilters,
): readonly ClaimRow[] {
  if (keyword.trim().length === 0 && !hasActiveFilters(filters)) {
    return claim.lines;
  }

  const matching = claim.lines.filter(
    (line) => matchesKeyword(line, keyword) && matchesFilters(line, filters),
  );

  return matching.length > 0 ? matching : claim.lines;
}

/**
 * What the Reason column shows on a claim's own row. The live payload carries the reason on the lines
 * only, so the claim row would be blank under a filter whose whole point is the reason; this lists the
 * distinct reasons of the lines being shown, in the order they first appear.
 */
export function summarizeReasons(lines: readonly ClaimRow[]): string | null {
  const reasons = lines
    .map((line) => line.reason?.trim() ?? '')
    .filter((reason, index, all) => reason.length > 0 && all.indexOf(reason) === index);

  return reasons.length === 0 ? null : reasons.join('; ');
}

export function matchesFilters(claim: ClaimRow, filters: ClaimFilters): boolean {
  return (
    contains(claim.claimNumber, filters.claimNumber) &&
    contains(claim.npi, filters.npi) &&
    contains(claim.icn, filters.icn) &&
    contains(claim.location, filters.location) &&
    contains(claim.procCode, filters.procCode) &&
    contains(claim.svcCode, filters.svcCode) &&
    contains(claim.reason, filters.reason) &&
    contains(claim.checkNumber, filters.checkNumber) &&
    matchesStatus(claim.status, filters.statuses) &&
    atLeast(claim.billedAmount, filters.billedAmount) &&
    atLeast(claim.medicareAllowedAmount, filters.medicareAllowedAmount) &&
    atLeast(claim.paidAmount, filters.paidAmount) &&
    overlapsRange(
      claim.serviceDateFrom,
      claim.serviceDateTo,
      filters.serviceDateFrom,
      filters.serviceDateTo,
    ) &&
    withinRange(claim.paymentDate, filters.paymentDateFrom, filters.paymentDateTo)
  );
}

export function sortClaims(claims: readonly ClaimRow[], sort: SortState | null): readonly ClaimRow[] {
  if (sort === null) {
    return claims;
  }

  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...claims].sort((left, right) => {
    const leftValue = left[sort.column];
    const rightValue = right[sort.column];
    const leftEmpty = isEmpty(leftValue);
    const rightEmpty = isEmpty(rightValue);

    // Empties are placed before the direction is applied, so they stay last either way instead of
    // taking over the top of the table when the sort is reversed.
    if (leftEmpty || rightEmpty) {
      return leftEmpty === rightEmpty ? 0 : leftEmpty ? 1 : -1;
    }

    return factor * compare(leftValue, rightValue);
  });
}

export { EMPTY_FILTERS };

function isEmpty(value: string | number | null): boolean {
  return value === null || value === '';
}

function compare(left: string | number | null, right: string | number | null): number {
  if (typeof left === 'number' && typeof right === 'number') {
    return left - right;
  }

  return String(left).localeCompare(String(right), 'en', { numeric: true });
}

function contains(value: string | null, term: string): boolean {
  const needle = term.trim().toLowerCase();
  return needle.length === 0 || (value ?? '').toLowerCase().includes(needle);
}

function matchesStatus(status: string | null, statuses: readonly string[]): boolean {
  return statuses.length === 0 || statuses.some((candidate) => candidate === status);
}

function atLeast(value: number | null, minimum: string): boolean {
  const threshold = Number(minimum.trim());
  if (minimum.trim().length === 0 || Number.isNaN(threshold)) {
    return true;
  }

  return value !== null && value >= threshold;
}

/**
 * Still accepts a half-open range even though the panel now refuses to submit one (US-026 AC-002 asks
 * for both ends). Kept permissive on purpose: this is the matcher, and it must not silently drop rows
 * if a caller ever applies a range the UI did not build.
 */
/**
 * Whether a claim's service span touches the requested window at all.
 *
 * A claim carries a span, not a day (ClaimRowMapper takes it from the entries), so testing only its
 * start date hides every claim that opened before the window and was still running inside it — a
 * provider filtering a recent window silently loses their long-running claims. Either end missing is
 * treated as open, so a one-sided filter keeps behaving as one.
 */
function overlapsRange(
  serviceFrom: string | null,
  serviceTo: string | null,
  from: string,
  to: string,
): boolean {
  if (from.length === 0 && to.length === 0) {
    return true;
  }

  // A claim with no dates at all cannot be shown to fall inside a window.
  const first = day(serviceFrom) ?? day(serviceTo);
  const second = day(serviceTo) ?? day(serviceFrom);
  if (first === null || second === null) {
    return false;
  }

  // Ordered rather than trusted: ClaimRowMapper fills the two ends from independent sources
  // (`lines.Min ?? headerFrom` and `lines.Max ?? headerTo`), so a claim whose entries supply one end
  // and whose header supplies the other can arrive with them the wrong way round.
  const start = first <= second ? first : second;
  const end = first <= second ? second : first;

  return (to.length === 0 || start <= to) && (from.length === 0 || end >= from);
}

function day(value: string | null): string | null {
  return value === null ? null : value.slice(0, 10);
}

function withinRange(value: string | null, from: string, to: string): boolean {
  if (from.length === 0 && to.length === 0) {
    return true;
  }
  if (value === null) {
    return false;
  }

  const day = value.slice(0, 10);
  return (from.length === 0 || day >= from) && (to.length === 0 || day <= to);
}

function isReversed(from: string, to: string): boolean {
  return from.length > 0 && to.length > 0 && from > to;
}
